import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import parserLogo from "../../assets/parserLogo.svg";
import { formatRecordingTime } from "./formatRecordingTime";

const PICTURE_IN_PICTURE_SIZE = {
  width: 119,
  height: 83,
};

export const supportsRecordingPictureInPicture = () =>
  typeof window !== "undefined" &&
  typeof window.documentPictureInPicture?.requestWindow === "function";

export const useRecordingPictureInPicture = () => {
  const [pictureInPictureWindow, setPictureInPictureWindow] = useState(null);
  const [isOpening, setIsOpening] = useState(false);
  const pictureInPictureWindowRef = useRef(null);
  const openPromiseRef = useRef(null);
  const shouldRemainOpenRef = useRef(false);

  const clearPictureInPictureWindow = useCallback((pipWindow) => {
    if (pictureInPictureWindowRef.current !== pipWindow) {
      return;
    }

    pictureInPictureWindowRef.current = null;
    setPictureInPictureWindow(null);
  }, []);

  const open = useCallback(() => {
    if (!supportsRecordingPictureInPicture()) {
      return Promise.resolve(null);
    }

    shouldRemainOpenRef.current = true;

    const currentWindow = pictureInPictureWindowRef.current;
    if (currentWindow && !currentWindow.closed) {
      currentWindow.focus?.();
      return Promise.resolve(currentWindow);
    }

    if (openPromiseRef.current) {
      return openPromiseRef.current;
    }

    setIsOpening(true);

    // This request must be made synchronously from the recording button click so
    // Chrome still considers it a user-initiated action.
    const openPromise = window.documentPictureInPicture
      .requestWindow(PICTURE_IN_PICTURE_SIZE)
      .then((pipWindow) => {
        if (!shouldRemainOpenRef.current) {
          pipWindow.close();
          return null;
        }

        pictureInPictureWindowRef.current = pipWindow;
        setPictureInPictureWindow(pipWindow);
        pipWindow.addEventListener("pagehide", () => clearPictureInPictureWindow(pipWindow), {
          once: true,
        });
        return pipWindow;
      })
      .catch((error) => {
        // The recorder should continue normally if Chrome blocks or cannot open
        // the optional floating indicator.
        console.info("Floating recording indicator was not opened:", error);
        return null;
      })
      .finally(() => {
        openPromiseRef.current = null;
        setIsOpening(false);
      });

    openPromiseRef.current = openPromise;
    return openPromise;
  }, [clearPictureInPictureWindow]);

  const close = useCallback(() => {
    shouldRemainOpenRef.current = false;
    const currentWindow = pictureInPictureWindowRef.current;
    if (currentWindow && !currentWindow.closed) {
      currentWindow.close();
    }
    clearPictureInPictureWindow(currentWindow);
  }, [clearPictureInPictureWindow]);

  useEffect(() => close, [close]);

  return {
    close,
    isOpen: Boolean(pictureInPictureWindow && !pictureInPictureWindow.closed),
    isOpening,
    isSupported: supportsRecordingPictureInPicture(),
    open,
    pictureInPictureWindow,
  };
};

const getIndicatorState = (recordingStatus, isStarting) => {
  if (isStarting) {
    return {
      color: "#783BFF",
      label: "Starting recording…",
      pulse: false,
    };
  }

  if (recordingStatus === "paused") {
    return {
      color: "#D97706",
      label: "Recording paused",
      pulse: false,
    };
  }

  return {
    color: "#DC2626",
    label: "Recording in progress",
    pulse: true,
  };
};

export const RecordingPictureInPicture = ({
  elapsedTime,
  isStarting,
  onReturnToKnowtex,
  pictureInPictureWindow,
  recordingStatus,
}) => {
  useEffect(() => {
    if (!pictureInPictureWindow) {
      return undefined;
    }

    const { document: pipDocument } = pictureInPictureWindow;
    pipDocument.title = "Knowtex recording";
    pipDocument.documentElement.style.height = "100%";
    pipDocument.body.style.height = "100%";
    pipDocument.body.style.margin = "0";
    pipDocument.body.style.overflow = "hidden";

    return () => {
      pipDocument.body.removeAttribute("style");
      pipDocument.documentElement.removeAttribute("style");
    };
  }, [pictureInPictureWindow]);

  if (!pictureInPictureWindow) {
    return null;
  }

  const indicatorState = getIndicatorState(recordingStatus, isStarting);

  return createPortal(
    <>
      <style>{`
        @keyframes knowtex-recording-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.34); }
          50% { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
        }
        * { box-sizing: border-box; }
        button:focus-visible { outline: 3px solid rgba(120, 59, 255, 0.35); outline-offset: 2px; }
      `}</style>
      <main
        style={{
          alignItems: "center",
          background: "linear-gradient(145deg, #FFFFFF 0%, #F7F4FF 100%)",
          color: "#25212E",
          display: "grid",
          fontFamily: '"Nunito Sans", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          gap: "24px",
          gridTemplateColumns: "25px auto",
          height: "100%",
          justifyContent: "center",
          padding: "9px 2px",
          width: "100%",
        }}
        title="Closing this indicator does not stop the recording."
      >
        <div
          aria-hidden="true"
          style={{
            alignItems: "center",
            background: "#FFFFFF",
            border: "1px solid #E7DEFF",
            borderRadius: "50%",
            boxShadow: "0 4px 12px rgba(91, 54, 175, 0.14)",
            display: "flex",
            height: "25px",
            justifyContent: "center",
            position: "relative",
            width: "25px",
          }}
        >
          <img alt="" src={parserLogo} style={{ height: "16px", width: "16px" }} />
          <span
            style={{
              animation: indicatorState.pulse
                ? "knowtex-recording-pulse 1.7s ease-in-out infinite"
                : "none",
              background: indicatorState.color,
              border: "2px solid #FFFFFF",
              borderRadius: "50%",
              bottom: "-1px",
              height: "9px",
              position: "absolute",
              right: "-1px",
              width: "9px",
            }}
          />
        </div>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              gap: "24px",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                aria-live="polite"
                style={{
                  color: indicatorState.color,
                  fontSize: "8px",
                  fontWeight: 800,
                  letterSpacing: "0.01em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {indicatorState.label}
              </div>
              <time
                aria-label={`Elapsed time ${formatRecordingTime(elapsedTime)}`}
                style={{
                  color: "#25212E",
                  display: "block",
                  fontSize: "13px",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 800,
                  letterSpacing: "0.02em",
                  lineHeight: 1.12,
                }}
              >
                {formatRecordingTime(elapsedTime)}
              </time>
            </div>
            <button
              aria-label="Return to Knowtex"
              onClick={onReturnToKnowtex}
              style={{
                alignItems: "center",
                background: "#783BFF",
                border: 0,
                borderRadius: "6px",
                color: "#FFFFFF",
                cursor: "pointer",
                display: "flex",
                flexShrink: 0,
                fontFamily: "inherit",
                fontSize: "9px",
                fontWeight: 800,
                height: "23px",
                justifyContent: "center",
                padding: 0,
                width: "46px",
              }}
              title="Return to Knowtex"
              type="button"
            >
              Return
            </button>
          </div>
        </div>
      </main>
    </>,
    pictureInPictureWindow.document.body
  );
};
